import { useCallback, useMemo, useState } from 'react';

import {
  makeAssetColor,
  makeAssetMemory,
  makeCreateBookmarkAsset,
  makeGetBookmarkAssets,
  makeVerifyBookmarkAsset,
} from '@mais-retorno-front/core-business/factories';
import { makeDeleteBookmarkAsset } from '@mais-retorno-front/core-business/factories/bookmark-asset/delete-bookmark-asset.factory';
import { IBookmarkAssetDetail } from '@mais-retorno-front/models/bookmark-asset-detail.model';
import { IFundComparisonBookmarkCreated } from '@mais-retorno-front/core-business/interfaces/fund-comparison-bookmark';

interface IUseBookmarkAssetResponse {
  assetsBookmark: IBookmarkAssetDetail[];
  loading: boolean;
  fundAlreadyFavorite: boolean;
  onGetBookmarkAssets: (
    token: string,
    page: number,
    pageSize: number
  ) => Promise<{ error: boolean; message: string }>;
  onVerifyBookmarkAsset: (asset_id: string) => Promise<void>;
  onCreateBookmarkAsset: (
    token: string,
    asset: {
      asset_type: string;
      identifier: string;
    }
  ) => Promise<{
    error: boolean;
    message: string;
  }>;
  onDeleteBookmarkAsset: (
    token: string,
    asset_id: string
  ) => Promise<{
    error: boolean;
    message: string;
  }>;
  totalPages: number;
}

export const useBookmarkAsset = (): IUseBookmarkAssetResponse => {
  const [assetsBookmark, setAssetsBookmark] = useState<IBookmarkAssetDetail[]>(
    []
  );
  const [fundAlreadyFavorite, setFundAlreadyFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1)

  const createBookmarkAssetUseCase = useMemo(
    () => makeCreateBookmarkAsset(),
    []
  );
  const verifyBookmarkAssetUseCase = useMemo(
    () => makeVerifyBookmarkAsset(),
    []
  );
  const deleteBookmarkAssetUseCase = useMemo(
    () => makeDeleteBookmarkAsset(),
    []
  );

  const colorRepository = useMemo(() => makeAssetColor(), []);

  const memoryRepository = useMemo(
    () => makeAssetMemory<IFundComparisonBookmarkCreated>(),
    []
  );

  const getAssetsBookmarkFundComparisonUseCase = useMemo(
    () =>
      makeGetBookmarkAssets({
        colorRepository,
        memoryRepository,
      }),
    []
  );

  const onGetBookmarkAssets = useCallback(
    async (token: string, page: number, pageSize: number): Promise<{ error: boolean; message: string }> => {
      setLoading(true);

      const { data, error, message, totalPages } =
        await getAssetsBookmarkFundComparisonUseCase.handle(token, page, pageSize);

      if (!error) {
        setTotalPages(totalPages || 1)
        setAssetsBookmark(data);
      }

      setLoading(false);

      return {
        error,
        message,
      };
    },
    [getAssetsBookmarkFundComparisonUseCase]
  );

  const onVerifyBookmarkAsset = useCallback(
    async (asset_id: string) => {
      setLoading(true);
      const { data, error, message } = await verifyBookmarkAssetUseCase.handle({
        asset_id,
      });

      if (data && !error) {
        setFundAlreadyFavorite(true);
      }

      setLoading(false);
    },
    [verifyBookmarkAssetUseCase]
  );

  const onCreateBookmarkAsset = useCallback(
    async (
      token: string,
      asset: {
        asset_type: string;
        identifier: string;
      }
    ): Promise<{ error: boolean; message: string }> => {
      setLoading(true);

      const { error, message } = await createBookmarkAssetUseCase.handle({
        token,
        asset,
      });

      setFundAlreadyFavorite(true);

      setLoading(false);
      return {
        error,
        message,
      };
    },
    [createBookmarkAssetUseCase]
  );

  const onDeleteBookmarkAsset = useCallback(
    async (
      token: string,
      asset_id: string
    ): Promise<{ error: boolean; message: string }> => {
      setLoading(true);

      const { error, message } = await deleteBookmarkAssetUseCase.handle({
        token,
        asset_id,
      });

      if (!error) {
        setAssetsBookmark(
          assetsBookmark.filter((eachAsset) => eachAsset.id !== asset_id)
        );
      }

      setLoading(false);

      return {
        error,
        message,
      };
    },
    [deleteBookmarkAssetUseCase]
  );

  return {
    assetsBookmark,
    loading,
    fundAlreadyFavorite,
    onGetBookmarkAssets,
    onVerifyBookmarkAsset,
    onCreateBookmarkAsset,
    onDeleteBookmarkAsset,
    totalPages
  };
};
